import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  clearSessionTokens,
  clearTokens,
  useAdminAuthStore,
} from "@charisol-spark/client-core";
import { api } from "@/lib/api";

/**
 * Resolve auth state on app startup — same reasoning as
 * packages/frontend/src/hooks/useAuthInitialization.ts (a persisted admin
 * with no access token in THIS tab may simply be a fresh tab, not a stale
 * session; the refresh token lives in localStorage, shared across tabs).
 * Trimmed to admin's simpler store (no onboarding/persona fields to worry
 * about re-triggering).
 */
export function useAuthInitialization() {
  const user = useAdminAuthStore((s) => s.user);
  const clearUser = useAdminAuthStore((s) => s.clearUser);
  const setUser = useAdminAuthStore((s) => s.setUser);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user) return;
    if (sessionStorage.getItem("spark_access_token")) return; // already live

    const hasRefreshToken = !!localStorage.getItem("spark_refresh_token");

    if (!hasRefreshToken) {
      clearUser();
      clearSessionTokens();
      queryClient.clear();
      return;
    }

    void (async () => {
      const resumed = await api.refresh();

      if (resumed) {
        const current = useAdminAuthStore.getState().user;
        if (current) setUser({ ...current });
        return;
      }

      clearUser();
      clearTokens();
      queryClient.clear();
    })();
    // Deliberately once on mount — see the frontend hook's comment for why.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
